/**
 * Blog › Autores: la foto, la biografía y los enlaces de cada autor. Las
 * entradas siguen firmando por nombre; aquí se completa el perfil que enseñan
 * en el sitio la pieza «Autor», «Autores», la ficha del autor y su página
 * (`/blog/author/<nombre>`). Los autores salen de las entradas que firman.
 */
import { useEffect, useRef, useState, type ComponentType } from 'react'
import { createPortal } from 'react-dom'
import { IconArrowLeft, IconBrandFacebook, IconBrandInstagram, IconBrandLinkedin, IconBrandPinterest, IconBrandThreads, IconBrandTiktok, IconBrandX, IconBrandYoutube, IconCamera, IconCheck, IconChevronDown, IconChevronRight, IconPlus, IconTrash, IconUser, IconUsers, IconWorld, IconX } from '@tabler/icons-react'
import { BLOG_LIMITS } from '@webbuilder/schemas'
import { deleteBlogAuthor, getBlogAuthors, saveBlogAuthor, type BlogAuthor } from '../../../lib/api'
import { ConfirmDialog } from '../ConfirmDialog'
import { useI18n } from '../../../lib/i18n'
import { useToast } from '../../Toast'
import { ImageLibraryPickerDialog } from '../../ImageLibraryPickerDialog'
import { errorMessage } from './offerShared'

const MAX_LINKS = 6

/* Las redes de un autor, con su logo (las mismas del elemento «Redes sociales»). */
const NETWORKS: { label: string; Icon: ComponentType<{ size?: number }> }[] = [
  { label: 'Instagram', Icon: IconBrandInstagram },
  { label: 'Facebook', Icon: IconBrandFacebook },
  { label: 'X', Icon: IconBrandX },
  { label: 'TikTok', Icon: IconBrandTiktok },
  { label: 'YouTube', Icon: IconBrandYoutube },
  { label: 'LinkedIn', Icon: IconBrandLinkedin },
  { label: 'Pinterest', Icon: IconBrandPinterest },
  { label: 'Threads', Icon: IconBrandThreads },
  { label: 'Web', Icon: IconWorld },
]

/**
 * Un enlace del autor: la red con su logo en un selector pegado al campo de la
 * dirección, como el prefijo del teléfono del asistente (`cwa-phone`).
 */
function SocialLinkField({ label, url, placeholder, onChange }: {
  label: string
  url: string
  placeholder: string
  onChange: (next: { label: string; url: string }) => void
}) {
  const [open, setOpen] = useState(false)
  /* El menú flota en el `body` (como el de estado de Ofertas): dentro del diálogo se cortaba. */
  const [at, setAt] = useState({ top: 0, left: 0 })
  const buttonRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') { event.stopPropagation(); close() } }
    window.addEventListener('keydown', onKey, true)
    window.addEventListener('resize', close)
    window.addEventListener('scroll', close, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      window.removeEventListener('resize', close)
      window.removeEventListener('scroll', close, true)
    }
  }, [open])
  const current = NETWORKS.find((network) => network.label.toLowerCase() === label.trim().toLowerCase()) ?? NETWORKS[NETWORKS.length - 1]!
  const toggle = () => {
    const rect = buttonRef.current?.getBoundingClientRect()
    if (rect && !open) {
      const height = NETWORKS.length * 36 + 12
      const below = window.innerHeight - rect.bottom - 8
      setAt({ top: below >= height ? rect.bottom + 6 : Math.max(8, rect.top - height - 6), left: Math.max(8, rect.left) })
    }
    setOpen((value) => !value)
  }
  return (
    <div className="cwa-phone" style={{ flex: 1, minWidth: 0 }}>
      <button ref={buttonRef} type="button" className="cwa-phone-code" aria-haspopup="listbox" aria-expanded={open} aria-label={current.label} onClick={toggle}>
        <current.Icon size={16} />
        <span className="cwa-phone-dial">{current.label}</span>
        <IconChevronDown size={14} />
      </button>
      <input className="cwa-phone-num" type="url" inputMode="url" value={url} maxLength={500} placeholder={placeholder} onChange={(event) => onChange({ label: current.label, url: event.target.value })} />
      {open ? createPortal(
        <>
          <div className="dash-status-select-backdrop is-portal" role="presentation" onClick={() => setOpen(false)} />
          <div className="dash-status-select-menu is-portal" role="listbox" aria-label={current.label} style={{ ...at, zIndex: 3000 }}>
            {NETWORKS.map((network) => (
              <button key={network.label} type="button" role="option" aria-selected={network.label === current.label} className={network.label === current.label ? 'is-on' : ''} onClick={() => { onChange({ label: network.label, url }); setOpen(false) }}>
                <network.Icon size={14} aria-hidden="true" />
                {network.label}
                {network.label === current.label ? <IconCheck size={14} aria-hidden="true" /> : null}
              </button>
            ))}
          </div>
        </>,
        document.body,
      ) : null}
    </div>
  )
}

export function BlogAuthorsDialog({ projectId, token, onClose, initialName }: {
  projectId: string
  token: string
  onClose: () => void
  /** Abrir directamente la ficha de este autor (desde el lápiz del selector de la entrada). */
  initialName?: string | undefined
}) {
  const { t } = useI18n()
  const { showToast } = useToast()
  const [authors, setAuthors] = useState<BlogAuthor[] | null>(null)
  const [editing, setEditing] = useState<BlogAuthor | null>(null)
  const [picking, setPicking] = useState(false)
  const [saving, setSaving] = useState(false)
  /* Una ficha nueva, sin nombre todavía: el nombre se escribe arriba. */
  const [creating, setCreating] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  /* El nombre con el que se abrió la ficha: si cambia, se renombra. */
  const [originalName, setOriginalName] = useState('')

  useEffect(() => {
    let cancelled = false
    /* Si el servidor no contesta en 10 s, se deja de esperar: la lista vacía deja crear uno. */
    const timeout = new Promise<never>((_resolve, reject) => { window.setTimeout(() => reject(new Error('timeout')), 10_000) })
    Promise.race([getBlogAuthors(projectId, token), timeout])
      .then((data) => {
        if (cancelled) return
        setAuthors(data.authors)
        const start = initialName ? data.authors.find((author) => author.name.toLocaleLowerCase() === initialName.toLocaleLowerCase()) : undefined
        if (start) open(start)
      })
      .catch(() => { if (!cancelled) setAuthors([]) })
    return () => { cancelled = true }
  }, [projectId, token, initialName])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !picking) onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, picking])

  function create() {
    setEditing({ name: '', avatarUrl: '', bio: '', links: [], posts: 0 })
    setOriginalName('')
    setCreating(true)
  }
  function open(author: BlogAuthor) {
    setEditing({ ...author, links: author.links.map((link) => ({ ...link })) })
    setOriginalName(author.name)
  }
  const closeEditor = () => { setEditing(null); setCreating(false); setOriginalName('') }

  async function remove() {
    if (!editing) return
    try {
      const result = await deleteBlogAuthor(projectId, editing.name, token)
      setAuthors((current) => (current ?? []).filter((author) => author.name.toLocaleLowerCase() !== editing.name.toLocaleLowerCase()))
      setConfirmDelete(false)
      closeEditor()
      showToast(t('blog.authors.profileDeleted', { count: result.posts }))
    } catch (error) {
      setConfirmDelete(false)
      showToast(errorMessage(error), 'error')
    }
  }

  async function save() {
    if (!editing) return
    const name = editing.name.trim()
    if (!name) return
    /* Uno nuevo con un nombre que ya existe edita a ése, no crea otro. */
    const existing = creating ? authors?.find((author) => author.name.toLocaleLowerCase() === name.toLocaleLowerCase()) : undefined
    const target = { ...editing, name: existing?.name ?? name, posts: existing?.posts ?? editing.posts }
    const renamedFrom = !creating && originalName && originalName !== target.name ? originalName : undefined
    setSaving(true)
    try {
      const links = editing.links.map((link) => ({ label: link.label.trim(), url: link.url.trim() })).filter((link) => link.label && link.url)
      await saveBlogAuthor(projectId, { name: target.name, avatarUrl: target.avatarUrl, bio: target.bio.trim(), links, ...(renamedFrom ? { previousName: renamedFrom } : {}) }, token)
      /* Uno nuevo (creado aquí, todavía sin entradas) se suma a la lista. */
      setAuthors((current) => {
        const list = (current ?? []).filter((author) => author.name !== renamedFrom)
        return list.some((author) => author.name === target.name)
          ? list.map((author) => (author.name === target.name ? { ...target, links } : author))
          : [...list, { ...target, links }]
      })
      closeEditor()
      showToast(t('blog.authors.saved'))
    } catch (error) {
      showToast(errorMessage(error), 'error')
    } finally {
      setSaving(false)
    }
  }

  /* Siempre un círculo, con foto o sin ella (entonces un icono sobre un fondo suave). */
  const avatar = (author: BlogAuthor, size: number) => (
    <span className="dash-blog-author-avatar" style={{ width: size, height: size, flex: `0 0 ${size}px` }} aria-hidden="true">
      {author.avatarUrl ? <img src={author.avatarUrl} alt="" /> : <IconUser size={Math.round(size / 2.4)} />}
    </span>
  )

  return (
    <div className="dash-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose() }}>
      <section className="dash-modal dash-coupon-modal" role="dialog" aria-modal="true" aria-label={t('blog.authors.title')}>
        <header className="dash-coupon-modal-head">
          {editing ? (
            <button type="button" className="dash-icon-btn" aria-label={t('blog.authors.back')} disabled={saving} onClick={closeEditor}><IconArrowLeft size={16} /></button>
          ) : (
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconUser size={18} /></span>
          )}
          <div>
            <h2>{editing ? (editing.name.trim() || t('blog.authors.newTitle')) : t('blog.authors.title')}</h2>
            <p>{editing ? t('blog.authors.editLead') : t('blog.authors.lead')}</p>
          </div>
          <button type="button" className="dash-icon-btn" aria-label={t('coupons.form.close')} disabled={saving} onClick={onClose}><IconX size={16} /></button>
        </header>

        <div className="dash-coupon-modal-body">
          {editing ? (
            <>
              {/* Foto y nombre en una fila: la foto se cambia pulsándola; el nombre,
                  también de uno que ya existe (cambia su firma en todas sus entradas). */}
              <div className="dash-coupon-block dash-blog-author-head">
                <button type="button" className="dash-blog-author-photo" aria-label={editing.avatarUrl ? t('blog.authors.changePhoto') : t('blog.authors.addPhoto')} title={editing.avatarUrl ? t('blog.authors.changePhoto') : t('blog.authors.addPhoto')} onClick={() => setPicking(true)}>
                  {avatar(editing, 72)}
                  <span className="dash-blog-author-photo-edit" aria-hidden="true"><IconCamera size={13} /></span>
                </button>
                <div className="dash-blog-author-name">
                  <label className="dash-field">
                    <span className="dash-coupon-label">{t('blog.authors.name')}</span>
                    <input autoFocus={creating} value={editing.name} maxLength={BLOG_LIMITS.author} placeholder={t('blog.authors.newPlaceholder')} onChange={(event) => setEditing({ ...editing, name: event.target.value })} />
                  </label>
                  {editing.avatarUrl ? (
                    <button type="button" className="dash-blog-author-unphoto" onClick={() => setEditing({ ...editing, avatarUrl: '' })}>{t('blog.authors.removePhoto')}</button>
                  ) : null}
                </div>
              </div>
              <div className="dash-coupon-block">
                <label className="dash-field">
                  <span className="dash-coupon-label">{t('blog.authors.bio')}</span>
                  <textarea rows={4} maxLength={600} value={editing.bio} placeholder={t('blog.authors.bioPlaceholder')} onChange={(event) => setEditing({ ...editing, bio: event.target.value })} />
                </label>
              </div>
              <div className="dash-coupon-block">
                <span className="dash-coupon-label">{t('blog.authors.links')}</span>
                {editing.links.map((link, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <SocialLinkField
                      label={link.label}
                      url={link.url}
                      placeholder={t('blog.authors.linkUrl')}
                      onChange={(next) => setEditing({ ...editing, links: editing.links.map((item, at) => (at === index ? next : item)) })}
                    />
                    <button type="button" className="dash-icon-btn" aria-label={t('blog.authors.removeLink')} onClick={() => setEditing({ ...editing, links: editing.links.filter((_item, at) => at !== index) })}><IconTrash size={15} /></button>
                  </div>
                ))}
                {editing.links.length < MAX_LINKS ? (
                  <button type="button" className="dash-secondary-btn" style={{ justifySelf: 'start' }} onClick={() => setEditing({ ...editing, links: [...editing.links, { label: 'Instagram', url: '' }] })}>
                    <IconPlus size={14} /> {t('blog.authors.addLink')}
                  </button>
                ) : null}
              </div>
            </>
          ) : (
            <div className="dash-coupon-block">
              {authors === null ? (
                <div role="status" aria-label={t('blog.authors.loading')} aria-busy="true">
                  <div className="dash-blog-authors-list dash-granular-loading" aria-hidden="true">
                    {[0, 1, 2].map((index) => (
                      <div key={index} className="dash-blog-author-row dash-blog-author-skeleton">
                        <span className="sk dash-blog-author-skeleton-avatar" />
                        <span className="dash-blog-author-copy">
                          <span className="sk sk-line sk-line-title" />
                          <span className="sk sk-line sk-line-sub" />
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : authors.length === 0 ? (
                /* Sin autores: un estado vacío con su botón, como las demás secciones. */
                <div className="dash-blog-authors-empty">
                  <IconUsers size={34} aria-hidden="true" />
                  <strong>{t('blog.authors.emptyTitle')}</strong>
                  <span>{t('blog.authors.none')}</span>
                  <button type="button" className="dash-primary-btn" onClick={create}><IconPlus size={15} /> {t('blog.authors.add')}</button>
                </div>
              ) : null}
              {authors === null || authors.length === 0 ? null : (
                <div className="dash-blog-authors-list">
                  {authors.map((author) => (
                    <button key={author.name} type="button" className="dash-blog-author-row" onClick={() => open(author)}>
                      {avatar(author, 40)}
                      <span className="dash-blog-author-copy">
                        <strong>{author.name}</strong>
                        <small>{author.bio || (author.posts === 1 ? t('blog.authors.postsOne') : t('blog.authors.posts', { count: author.posts }))}</small>
                      </span>
                      {author.bio ? <span className="dash-blog-author-count">{author.posts === 1 ? t('blog.authors.postsOne') : t('blog.authors.posts', { count: author.posts })}</span> : null}
                      <IconChevronRight size={16} aria-hidden="true" />
                    </button>
                  ))}
                  <button type="button" className="dash-blog-author-row is-add" onClick={create}>
                    <IconPlus size={16} aria-hidden="true" />
                    <span>{t('blog.authors.add')}</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <footer>
          {editing ? (
            <>
              {/* Eliminar, a la izquierda y discreto (sólo uno que ya existe). */}
              {!creating ? (
                <button type="button" className="dash-danger-btn" style={{ marginRight: 'auto' }} disabled={saving} onClick={() => setConfirmDelete(true)}>
                  <IconTrash size={14} /> {t('blog.authors.delete')}
                </button>
              ) : null}
              <button type="button" className="dash-secondary-btn" disabled={saving} onClick={closeEditor}>{t('coupons.form.cancel')}</button>
              <button type="button" className="dash-primary-btn" disabled={saving || !editing.name.trim()} onClick={() => { void save() }}>{saving ? t('coupons.form.saving') : t('coupons.form.save')}</button>
            </>
          ) : (
            <button type="button" className="dash-secondary-btn" onClick={onClose}>{t('coupons.form.close')}</button>
          )}
        </footer>
      </section>
      {confirmDelete && editing ? (
        <ConfirmDialog
          tone="danger"
          title={t('blog.authors.profileDeleteTitle', { name: editing.name })}
          message={editing.posts ? t('blog.authors.profileDeleteBody', { count: editing.posts }) : t('blog.authors.deleteBodyNone')}
          confirmLabel={t('blog.authors.delete')}
          onConfirm={remove}
          onCancel={() => setConfirmDelete(false)}
        />
      ) : null}
      {picking && editing ? (
        <ImageLibraryPickerDialog token={token} projectId={projectId} onSelect={(url: string) => { setEditing({ ...editing, avatarUrl: url }); setPicking(false) }} onClose={() => setPicking(false)} />
      ) : null}
    </div>
  )
}
